import type { Invoice, Otherpromo, PaymentOption } from "@be-miniapp/api"
import type {
  DriverAcceptedData,
  FullEngagementInfo,
  TripState,
} from "@be-miniapp/types"

import type { CurrentRideData, RideLocation } from "./ride-data"

interface RidePickupDropoffSlice {
  pickup: RideLocation | undefined
  dropoff: RideLocation | undefined
}

function selectRidePickupDropoff(
  state: CurrentRideData
): RidePickupDropoffSlice {
  return {
    pickup: state.pickup,
    dropoff: state.dropoff,
  }
}

interface RideRouteSelectionActionsSlice {
  setPickup: (pickup: RideLocation | undefined) => void
  setDropoff: (dropoff: RideLocation | undefined) => void
  setSavedLoc: (savedLoc: RideLocation | undefined) => void
}

function selectRideRouteSelectionActions(
  state: CurrentRideData
): RideRouteSelectionActionsSlice {
  return {
    setPickup: state.setPickup,
    setDropoff: state.setDropoff,
    setSavedLoc: state.setSavedLoc,
  }
}

interface RideBookingSlice {
  vehicleType: number
  paymentMethod: PaymentOption | undefined
  preferredPaymentMethod: number | undefined
  currentPromo: Otherpromo | undefined
  isCurrentPromoTouched: boolean
  isFromBestCoupon: boolean
  note: string | undefined
  invoice: Invoice | undefined
}

function selectRideBooking(state: CurrentRideData): RideBookingSlice {
  return {
    vehicleType: state.vehicleType,
    paymentMethod: state.paymentMethod,
    preferredPaymentMethod: state.preferredPaymentMethod,
    currentPromo: state.currentPromo,
    isCurrentPromoTouched: state.isCurrentPromoTouched,
    isFromBestCoupon: state.isFromBestCoupon,
    note: state.note,
    invoice: state.invoice,
  }
}

interface RideBookingActionsSlice {
  setVehicleType: (vehicleType: number) => void
  setPreferredPaymentMethod: (preferredPaymentMethod: number) => void
  setCurrentPromo: CurrentRideData["setCurrentPromo"]
  setPaymentMethod: (paymentMethod: PaymentOption) => void
  setNote: (note: string | undefined) => void
  setInvoice: (invoice?: Invoice) => void
}

function selectRideBookingActions(
  state: CurrentRideData
): RideBookingActionsSlice {
  return {
    setVehicleType: state.setVehicleType,
    setPreferredPaymentMethod: state.setPreferredPaymentMethod,
    setCurrentPromo: state.setCurrentPromo,
    setPaymentMethod: state.setPaymentMethod,
    setNote: state.setNote,
    setInvoice: state.setInvoice,
  }
}

interface RideLiveSessionSlice {
  sessionId: number | undefined
  lastMsgId: string | null | undefined
  engagement: (DriverAcceptedData & FullEngagementInfo) | undefined
  state: TripState | undefined
}

function selectRideLiveSession(state: CurrentRideData): RideLiveSessionSlice {
  return {
    sessionId: state.sessionId,
    lastMsgId: state.lastMsgId,
    engagement: state.engagement,
    state: state.state,
  }
}

interface RideLiveSessionActionsSlice {
  setSessionId: (sessionId: number) => void
  setEngagement: (
    sessionData: DriverAcceptedData | FullEngagementInfo,
    reset?: boolean
  ) => void
  setCurrentState: (state: TripState | undefined) => void
  setLastMsgId: (lastMsgId: string | null) => void
  clearEngagement: () => void
  resetRideState: () => void
  hydrateInTripSession: CurrentRideData["hydrateInTripSession"]
}

function selectRideLiveSessionActions(
  state: CurrentRideData
): RideLiveSessionActionsSlice {
  return {
    setSessionId: state.setSessionId,
    setEngagement: state.setEngagement,
    setCurrentState: state.setCurrentState,
    setLastMsgId: state.setLastMsgId,
    clearEngagement: state.clearEngagement,
    resetRideState: state.resetRideState,
    hydrateInTripSession: state.hydrateInTripSession,
  }
}

export {
  selectRidePickupDropoff,
  selectRideRouteSelectionActions,
  selectRideBooking,
  selectRideBookingActions,
  selectRideLiveSession,
  selectRideLiveSessionActions,
}
export type {
  RidePickupDropoffSlice,
  RideRouteSelectionActionsSlice,
  RideBookingSlice,
  RideBookingActionsSlice,
  RideLiveSessionSlice,
  RideLiveSessionActionsSlice,
}
